import React from "react";

const WARNING_LABELS = {
    uses_approximated_options: "Approximated options pricing — real options history unavailable",
    synthetic_heavy:           "Synthetic-heavy validation — limited empirical data",
    low_trade_count:           "Low trade count — confidence intervals are wide",
    insufficient_oos:          "Insufficient out-of-sample evidence",
    widening_ci:               "Confidence intervals are widening",
    provider_limited:          "Provider limitations affect historical data quality",
    no_real_options_history:   "No real options history — synthetic data only",
    approximated_iv:           "Implied volatility approximated from historical vol",
};

function pct(value) {
    if (value == null || Number.isNaN(Number(value))) return "—";
    return `${Math.round(Number(value) * 100)}%`;
}

function deriveLegacyWarnings(realism) {
    return Object.entries(realism || {})
        .filter(([, v]) => v === true)
        .map(([key]) => WARNING_LABELS[key] || key.replace(/_/g, " "));
}

function buildSummary(realism) {
    if (!realism) return null;

    const warnings = [
        ...new Set([
            ...(Array.isArray(realism.missing_data_warnings) ? realism.missing_data_warnings : []),
            ...deriveLegacyWarnings(realism),
        ].filter(Boolean)),
    ];

    const activeFlags = Object.entries(realism.realism_flags || {})
        .filter(([, v]) => Boolean(v))
        .map(([key]) => key.replace(/_/g, " "));

    const hasStructuredScores = [
        realism.realism_score,
        realism.synthetic_dependency_ratio,
        realism.provider_completeness_score,
    ].some((v) => v != null);

    if (!warnings.length && !activeFlags.length && !hasStructuredScores) return null;

    return {
        title: realism.strategy_count > 1 ? "Validation Realism Snapshot" : "Simulation Realism",
        label: realism.realism_label || null,
        realismScore: realism.realism_score,
        syntheticDependencyRatio: realism.synthetic_dependency_ratio,
        providerCompletenessScore: realism.provider_completeness_score,
        deploymentImplication: realism.deployment_implication || null,
        providersUsed: Array.isArray(realism.provider_ids_used) ? realism.provider_ids_used : [],
        warnings,
    };
}

export default function RealistWarnings({ realism }) {
    const summary = buildSummary(realism);
    if (!summary) return null;

    return (
        <div className="rd-realism-panel">
            <div className="rd-realism-header">
                <div>
                    <div className="rd-realism-kicker">
                        {summary.title}
                    </div>
                    {summary.label && (
                        <div className="rd-realism-title">
                            {summary.label}
                        </div>
                    )}
                </div>
                <div className="rd-realism-chips">
                    {summary.realismScore != null && (
                        <span className="rd-realism-chip">
                            Realism {pct(summary.realismScore)}
                        </span>
                    )}
                    {summary.syntheticDependencyRatio != null && (
                        <span className="rd-realism-chip">
                            Synthetic {pct(summary.syntheticDependencyRatio)}
                        </span>
                    )}
                    {summary.providerCompletenessScore != null && (
                        <span className="rd-realism-chip">
                            Provider {pct(summary.providerCompletenessScore)}
                        </span>
                    )}
                </div>
            </div>

            {summary.deploymentImplication && (
                <div className="rd-realism-copy">
                    {summary.deploymentImplication}
                </div>
            )}

            {summary.providersUsed.length > 0 && (
                <div className="rd-realism-providers">
                    Providers: {summary.providersUsed.join(", ")}
                </div>
            )}

            {summary.warnings.length > 0 && (
                <ul className="rd-realism-warnings">
                    {summary.warnings.slice(0, 5).map((warning) => (
                        <li key={warning}>
                            {warning}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
